import type { CardBorderFillTone } from "@assetwatch/ui";
import type { TFunction } from "i18next";
import L from "leaflet";
import type {
  EnterpriseOverviewFacility,
  EnterpriseOverviewResponse,
} from "../../api/enterpriseClient";
import type {
  BreakdownFilter,
  BreakdownSortOption,
  EnterpriseOverviewMetrics,
  FacilityStatus,
} from "./types";

export const ALL_REGIONS_VALUE = "all";
const UNGROUPED_REGION_VALUE = "__ungrouped__";
const REGION_GROUP_PREFIX = "group:";

export const formatCurrency = (value: number, locale: string) =>
  new Intl.NumberFormat(locale, {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(value);

export const formatNumber = (value: number, locale: string) =>
  new Intl.NumberFormat(locale).format(value);

export function getFacilityDisplayName(
  facility: Pick<EnterpriseOverviewFacility, "facilityName" | "city" | "state">,
) {
  return facility.facilityName.trim()
    ? facility.facilityName
    : `${facility.city}, ${facility.state}`;
}

export function getFacilityRegion(facility: EnterpriseOverviewFacility) {
  return getFacilityRegions(facility)[0];
}

export function getRegionOptions(
  allRegionsLabel: string,
  facilityGroupBreakdown?: EnterpriseOverviewResponse["facilityGroupBreakdown"],
) {
  const groupNames = getFacilityGroupOptionNames(facilityGroupBreakdown);

  return [
    {
      value: ALL_REGIONS_VALUE,
      label: allRegionsLabel,
    },
    ...groupNames
      .sort((left, right) => left.localeCompare(right))
      .map((groupName) => ({
        value: getRegionOptionValue(groupName),
        label: groupName,
      })),
  ];
}

export function filterFacilitiesByRegion(
  facilities: EnterpriseOverviewFacility[],
  region: string,
) {
  if (region === ALL_REGIONS_VALUE) return facilities;
  const facilityRegion = getRegionFromOptionValue(region);
  return facilities.filter((facility) =>
    getFacilityRegions(facility).includes(facilityRegion),
  );
}

export function getOverviewMetricsForFacilities(
  facilities: EnterpriseOverviewFacility[],
  fallbackMetrics: EnterpriseOverviewMetrics,
): EnterpriseOverviewMetrics {
  return {
    ...fallbackMetrics,
    facilityCount: facilities.length,
    machineCount: nonNegativeCount(sumFacilities(facilities, "machineCount")),
    allGoodMachineCount: nonNegativeCount(
      sumFacilities(facilities, "allGoodMachineCount"),
    ),
    watchingMachineCount: nonNegativeCount(
      sumFacilities(facilities, "watchingMachineCount"),
    ),
    maintenanceRecommendedMachineCount: nonNegativeCount(
      sumFacilities(facilities, "maintenanceRecommendedMachineCount"),
    ),
    watchingAlertsCount: nonNegativeCount(
      sumFacilities(facilities, "watchingAlertsCount"),
    ),
    totalSaves: nonNegativeCount(sumFacilities(facilities, "totalSaves")),
    totalCostSavings: nonNegativeCount(
      sumFacilities(facilities, "totalCostSavings"),
    ),
    lifetimeSaves: nonNegativeCount(sumFacilities(facilities, "lifetimeSaves")),
    lifetimeCostSavings: nonNegativeCount(
      sumFacilities(facilities, "lifetimeCostSavings"),
    ),
    openAlertCostOfInaction: nonNegativeCount(
      sumFacilities(facilities, "openAlertCostOfInaction"),
    ),
    averageRoi: averageNullable(facilities.map((facility) => facility.roi)),
    averageMttrHours: averageNullable(
      facilities.map((facility) => facility.mttrHours),
    ),
    averageMtbfDays: averageNullable(
      facilities.map((facility) => facility.mtbfDays),
    ),
    averageTimeToCommentHours: averageNullable(
      facilities.map((facility) => facility.meanTimeToCommentHours),
    ),
    averageTimeToRespondHours: averageNullable(
      facilities.map((facility) => facility.meanTimeToRespondHours),
    ),
  };
}

export function getFacilityStatus(
  facility: EnterpriseOverviewFacility,
): FacilityStatus {
  const score = getFacilityScore(facility);

  if (score < 60) return "critical";
  if (score < 80) return "atRisk";
  return "healthy";
}

export function getStatusCounts(facilities: EnterpriseOverviewFacility[]) {
  return facilities.reduce(
    (counts, facility) => {
      counts[getFacilityStatus(facility)] += 1;
      return counts;
    },
    { healthy: 0, atRisk: 0, critical: 0 },
  );
}

export function getFacilityScore(facility: EnterpriseOverviewFacility) {
  const score = Math.round(
    (facility.allGoodMachineCount / Math.max(facility.machineCount, 1)) * 100,
  );
  return Math.min(100, Math.max(0, score));
}

export function getSortedFacilities(facilities: EnterpriseOverviewFacility[]) {
  return [...facilities].sort((a, b) => {
    const statusScore =
      b.maintenanceRecommendedMachineCount -
      a.maintenanceRecommendedMachineCount;
    if (statusScore !== 0) return statusScore;
    return b.openAlertCostOfInaction - a.openAlertCostOfInaction;
  });
}

export type NormalizedSearchQuery = {
  text: string;
  compact: string;
};

export function matchesFacilitySearch(
  facility: EnterpriseOverviewFacility,
  query: NormalizedSearchQuery,
  t: TFunction,
) {
  if (!query.text) return true;

  const status = getFacilityStatus(facility);
  const searchableText = [
    getFacilityDisplayName(facility),
    facility.facilityName,
    facility.city,
    facility.state,
    facility.facilityGroupNames.join(" "),
    status,
    t(`enterprise.overview.statusChip.${status}`),
  ]
    .filter(Boolean)
    .join(" ");

  const normalizedSearchableText = normalizeSearchText(searchableText);
  return (
    normalizedSearchableText.text.includes(query.text) ||
    (query.compact.length > 0 &&
      normalizedSearchableText.compact.includes(query.compact))
  );
}

export function normalizeSearchText(value: string): NormalizedSearchQuery {
  const text = value.trim().toLowerCase();
  return {
    text,
    compact: text.replace(/[^a-z0-9]/g, ""),
  };
}

export function isBreakdownFilter(value: string): value is BreakdownFilter {
  return (
    value === "all" ||
    value === "healthy" ||
    value === "atRisk" ||
    value === "critical"
  );
}

export function isBreakdownSortOption(
  value: string | null,
): value is BreakdownSortOption {
  return (
    value === "reliabilityAsc" ||
    value === "reliabilityDesc" ||
    value === "openAlertsDesc" ||
    value === "meanTimeDesc" ||
    value === "facilityAsc"
  );
}

export function sortFacilities(
  facilities: EnterpriseOverviewFacility[],
  sortOption: BreakdownSortOption,
) {
  return [...facilities].sort((left, right) => {
    if (sortOption === "reliabilityAsc") {
      return getFacilityScore(left) - getFacilityScore(right);
    }
    if (sortOption === "reliabilityDesc") {
      return getFacilityScore(right) - getFacilityScore(left);
    }
    if (sortOption === "openAlertsDesc") {
      return getOpenAlertCount(right) - getOpenAlertCount(left);
    }
    if (sortOption === "meanTimeDesc") {
      return (right.mttrHours ?? -1) - (left.mttrHours ?? -1);
    }
    return getFacilityDisplayName(left).localeCompare(
      getFacilityDisplayName(right),
    );
  });
}

export function sortFacilitiesByStatus(
  facilities: EnterpriseOverviewFacility[],
) {
  return [...facilities].sort((left, right) => {
    const statusDelta =
      getStatusSortRank(getFacilityStatus(left)) -
      getStatusSortRank(getFacilityStatus(right));
    if (statusDelta !== 0) return statusDelta;

    const scoreDelta = getFacilityScore(left) - getFacilityScore(right);
    if (scoreDelta !== 0) return scoreDelta;

    const alertDelta = getOpenAlertCount(right) - getOpenAlertCount(left);
    if (alertDelta !== 0) return alertDelta;

    return getFacilityDisplayName(left).localeCompare(
      getFacilityDisplayName(right),
    );
  });
}

export function getOpenAlertCount(facility: EnterpriseOverviewFacility) {
  return Math.max(
    0,
    facility.maintenanceRecommendedMachineCount + facility.watchingAlertsCount,
  );
}

export function getFacilityRegionLabel(facility: EnterpriseOverviewFacility) {
  const facilityRegions = getFacilityRegions(facility);
  if (facilityRegions[0] !== UNGROUPED_REGION_VALUE) {
    return facilityRegions[0];
  }
  if (facility.city && facility.state) return facility.state;
  return facility.state ?? facility.city ?? facility.facilityName;
}

function getFacilityRegions(facility: EnterpriseOverviewFacility) {
  const regions = Array.from(
    new Set(
      facility.facilityGroupNames
        .map((region) => region.trim())
        .filter(Boolean),
    ),
  );

  return regions.length > 0 ? regions : [UNGROUPED_REGION_VALUE];
}

function getFacilityGroupOptionNames(
  facilityGroupBreakdown?: EnterpriseOverviewResponse["facilityGroupBreakdown"],
) {
  const groupNames = new Set<string>();

  for (const group of facilityGroupBreakdown ?? []) {
    const groupName = group.facilityGroupName.trim();
    if (groupName) groupNames.add(groupName);
  }

  return Array.from(groupNames);
}

function getRegionOptionValue(region: string) {
  return region === UNGROUPED_REGION_VALUE
    ? UNGROUPED_REGION_VALUE
    : `${REGION_GROUP_PREFIX}${region}`;
}

function getRegionFromOptionValue(value: string) {
  return value.startsWith(REGION_GROUP_PREFIX)
    ? value.slice(REGION_GROUP_PREFIX.length)
    : value;
}

export function formatMeanTimeToResolve(
  mttrHours: number | null,
  locale: string,
  t: TFunction<"translation", undefined>,
) {
  if (mttrHours === null) {
    return t("enterprise.overview.meanTimeToResolveUnknown");
  }

  return t("enterprise.overview.meanTimeToResolveHours", {
    hours: new Intl.NumberFormat(locale, {
      maximumFractionDigits: 1,
    }).format(mttrHours),
  });
}

export function formatMeanTimeToResolveCompact(
  mttrHours: number | null,
  locale: string,
  t: TFunction<"translation", undefined>,
) {
  if (mttrHours === null) {
    return t("enterprise.overview.meanTimeToResolveUnknown");
  }

  return t("enterprise.overview.hoursToResolveCompact", {
    hours: new Intl.NumberFormat(locale, {
      maximumFractionDigits: 1,
    }).format(mttrHours),
  });
}

export function getStatusTone(status: FacilityStatus) {
  if (status === "healthy") return "positive";
  if (status === "atRisk") return "warning";
  return "negative";
}

export function getStatusCardBorderTone(
  status: FacilityStatus,
): CardBorderFillTone {
  return getStatusTone(status);
}

export function getStatusTextClassName(status: FacilityStatus) {
  if (status === "critical") return "text-error";
  if (status === "atRisk") return "text-warning";
  return "text-positive";
}

export function getFacilityMapScreenReaderText(
  facility: EnterpriseOverviewFacility,
  locale: string,
) {
  return formatCurrency(facility.openAlertCostOfInaction, locale);
}

export function getLocatedFacilities(facilities: EnterpriseOverviewFacility[]) {
  return facilities.flatMap((facility) => {
    const coordinates = getFacilityCoordinates(facility);
    if (coordinates === null) return [];
    return [
      {
        facility,
        latitude: coordinates[0],
        longitude: coordinates[1],
      },
    ];
  });
}

export function createFacilityMarkerIcon(status: FacilityStatus) {
  const marker = getMarkerVisual(status);

  return L.divIcon({
    className: "",
    iconSize: [marker.size, marker.size],
    iconAnchor: [marker.size / 2, marker.size / 2],
    popupAnchor: [0, -(marker.size / 2 + 8)],
    html: `<span style="display:block;position:relative;width:${marker.size}px;height:${marker.size}px;border-radius:9999px;background:${marker.color};border:${marker.halo}px solid hsl(var(--surface-surface-container-lowest));box-shadow:var(--effect-elevation-2);"><span style="position:absolute;left:50%;top:50%;width:${marker.center}px;height:${marker.center}px;border-radius:9999px;background:hsl(var(--surface-surface-container-lowest));transform:translate(-50%,-50%);"></span></span>`,
  });
}

function sumFacilities(
  facilities: EnterpriseOverviewFacility[],
  key: keyof Pick<
    EnterpriseOverviewFacility,
    | "machineCount"
    | "allGoodMachineCount"
    | "watchingMachineCount"
    | "maintenanceRecommendedMachineCount"
    | "watchingAlertsCount"
    | "totalSaves"
    | "totalCostSavings"
    | "lifetimeSaves"
    | "lifetimeCostSavings"
    | "openAlertCostOfInaction"
  >,
) {
  return facilities.reduce((total, facility) => total + facility[key], 0);
}

export function nonNegativeCount(value: number) {
  return Math.max(0, value);
}

function averageNullable(values: Array<number | null>) {
  const numericValues = values.filter(
    (value): value is number => value !== null,
  );
  if (numericValues.length === 0) return null;
  return (
    numericValues.reduce((total, value) => total + value, 0) /
    numericValues.length
  );
}

function getStatusSortRank(status: FacilityStatus) {
  if (status === "critical") return 0;
  if (status === "atRisk") return 1;
  return 2;
}

function getFacilityCoordinates(
  facility: EnterpriseOverviewFacility,
): [number, number] | null {
  if (facility.latitude === null || facility.longitude === null) return null;

  const latitude = Number(facility.latitude);
  const longitude = Number(facility.longitude);

  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null;
  if (latitude === 0 && longitude === 0) return null;
  return latitude >= -90 &&
    latitude <= 90 &&
    longitude >= -180 &&
    longitude <= 180
    ? [latitude, longitude]
    : null;
}

function getMarkerVisual(status: FacilityStatus) {
  const color = getMarkerColor(status);

  if (status === "critical") {
    return { color, size: 42, center: 18, halo: 4 };
  }
  if (status === "atRisk") {
    return { color, size: 30, center: 12, halo: 3 };
  }
  return { color, size: 26, center: 10, halo: 3 };
}

function getMarkerColor(status: FacilityStatus) {
  if (status === "critical") return "var(--color-negative)";
  if (status === "atRisk") return "var(--color-accessibility-warning)";
  return "var(--color-positive)";
}
